import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TMUA_PEARSON_TOKENS, getTmuaPearsonChrome } from '../constants/tmuaPearsonTokens';
import {
  getMostLeastRestWidth,
  getMostLeastShrinkWidths,
  getMostLeastTypeScale,
} from '../utils/mostLeastShrinkLayout';

function isMostLeastGhostCoarse() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(pointer: coarse)').matches
    || window.matchMedia('(max-width: 768px)').matches;
}

function plainText(html) {
  return String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

function slotLabelLines(label) {
  if (label === 'Most Appropriate') return ['Most', 'Appropriate'];
  if (label === 'Least Appropriate') return ['Least', 'Appropriate'];
  return [label];
}

export default function TmuaPearsonDragMostLeast({
  options,
  value,
  onPlace,
  onClear,
  readOnly,
  colorScheme,
  prepareExamHtml,
  questionId,
  compactLayout = false,
}) {
  const T = TMUA_PEARSON_TOKENS;
  const chrome = getTmuaPearsonChrome(colorScheme);
  const isDefault = !colorScheme || colorScheme === 'pearsonDefault';
  const dropzoneEmptyBg = isDefault ? T.mlDropzoneEmptyBg : chrome.ynDropzoneEmptyBg;
  const trayBg = isDefault ? T.mlTrayBg : chrome.ynTrayBg;
  const restPx = getMostLeastRestWidth(T);

  const rootRef = useRef(null);
  const slotsRef = useRef(null);
  const trayRef = useRef(null);
  const dragRef = useRef(null);
  const paneScrollRef = useRef(null);
  const autoScrollRafRef = useRef(null);
  const [ghost, setGhost] = useState(null);
  const ghostRafRef = useRef(null);
  const pendingGhostRef = useRef(null);
  const shrinkRafRef = useRef(null);

  const list = options || [];
  const answers = value || {};
  const mostId = answers.most_option_id ? String(answers.most_option_id) : '';
  const leastId = answers.least_option_id ? String(answers.least_option_id) : '';

  const optionById = useCallback((id) => (
    (options || []).find((opt) => String(opt.id) === String(id)) || null
  ), [options]);

  const scheduleGhostRender = useCallback(() => {
    if (ghostRafRef.current) return;
    ghostRafRef.current = requestAnimationFrame(() => {
      ghostRafRef.current = null;
      if (pendingGhostRef.current) setGhost({ ...pendingGhostRef.current });
    });
  }, []);

  const resolvePane = useCallback(() => {
    if (paneScrollRef.current) return paneScrollRef.current;
    let el = rootRef.current;
    while (el && el !== document.body) {
      if (el.classList && el.classList.contains('ivori-exam-question-pane')) {
        paneScrollRef.current = el;
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRafRef.current) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  }, []);

  const maybeAutoScroll = useCallback((clientY) => {
    /* Nested slots/tray overflow must not move the captured card during a drag. */
    if (compactLayout) return;
    const pane = resolvePane();
    if (!pane) return;
    const rect = pane.getBoundingClientRect();
    const edge = T.ynAutoScrollEdgePx;
    const distTop = clientY - rect.top;
    const distBottom = rect.bottom - clientY;
    let speed = 0;
    if (distTop >= 0 && distTop < edge) {
      speed = -Math.round((edge - distTop) / edge * 12);
    } else if (distBottom >= 0 && distBottom < edge) {
      speed = Math.round((edge - distBottom) / edge * 12);
    }
    stopAutoScroll();
    if (speed !== 0) {
      const step = () => {
        pane.scrollTop += speed;
        autoScrollRafRef.current = requestAnimationFrame(step);
      };
      autoScrollRafRef.current = requestAnimationFrame(step);
    }
  }, [T.ynAutoScrollEdgePx, compactLayout, resolvePane, stopAutoScroll]);

  const handlersRef = useRef({});

  const endDrag = useCallback(() => {
    stopAutoScroll();
    const d = dragRef.current;
    if (d && d.captureEl) {
      if (d.onMove) d.captureEl.removeEventListener('pointermove', d.onMove);
      if (d.onUp) d.captureEl.removeEventListener('pointerup', d.onUp);
      if (d.onCancel) d.captureEl.removeEventListener('pointercancel', d.onCancel);
      if (d.pointerId != null && typeof d.captureEl.releasePointerCapture === 'function') {
        try {
          if (!d.captureEl.hasPointerCapture || d.captureEl.hasPointerCapture(d.pointerId)) {
            d.captureEl.releasePointerCapture(d.pointerId);
          }
        } catch (err) { /* ignore */ }
      }
    }
    dragRef.current = null;
    pendingGhostRef.current = null;
    setGhost(null);
    if (typeof document !== 'undefined') document.body.style.cursor = '';
  }, [stopAutoScroll]);

  const onPointerMoveWindow = useCallback((e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    pendingGhostRef.current = {
      x: e.clientX,
      y: e.clientY,
      text: d.text,
      width: d.tileWidth,
      height: d.tileHeight,
      transform: d.ghostTransform,
    };
    scheduleGhostRender();
    if (!d.started) {
      const dx = e.clientX - d.originX;
      const dy = e.clientY - d.originY;
      if (Math.hypot(dx, dy) < T.ynDragThresholdPx) return;
      d.started = true;
    }
    e.preventDefault();
    maybeAutoScroll(e.clientY);
  }, [T.ynDragThresholdPx, scheduleGhostRender, maybeAutoScroll]);

  const onPointerUpWindow = useCallback((e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    if (d.started) {
      const el = typeof document !== 'undefined' ? document.elementFromPoint(e.clientX, e.clientY) : null;
      const dropzoneEl = el && el.closest ? el.closest('[data-ml-dropzone-slot]') : null;
      const targetSlot = dropzoneEl ? dropzoneEl.getAttribute('data-ml-dropzone-slot') : null;
      if (targetSlot) {
        onPlace(targetSlot, d.optionId);
      } else if (d.source === 'bar') {
        onClear(d.fromSlot);
      }
    }
    endDrag();
  }, [onPlace, onClear, endDrag]);

  const onPointerCancelWindow = useCallback((e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    endDrag();
  }, [endDrag]);

  handlersRef.current = { onPointerMoveWindow, onPointerUpWindow, onPointerCancelWindow };

  useEffect(() => {
    const onMove = (e) => handlersRef.current.onPointerMoveWindow(e);
    const onUp = (e) => handlersRef.current.onPointerUpWindow(e);
    const onCancel = (e) => handlersRef.current.onPointerCancelWindow(e);
    const optsMove = { capture: true, passive: false };
    const optsUp = { capture: true };
    document.addEventListener('pointermove', onMove, optsMove);
    document.addEventListener('pointerup', onUp, optsUp);
    document.addEventListener('pointercancel', onCancel, optsUp);
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      document.removeEventListener('pointermove', onMove, optsMove);
      document.removeEventListener('pointerup', onUp, optsUp);
      document.removeEventListener('pointercancel', onCancel, optsUp);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && dragRef.current) endDrag();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [endDrag]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    if (typeof ResizeObserver === 'undefined') return undefined;

    let observer = null;

    const parentContentWidth = () => {
      const parent = root.parentElement;
      if (!parent) return root.clientWidth;
      const cs = typeof window !== 'undefined' && window.getComputedStyle
        ? window.getComputedStyle(parent)
        : null;
      const pad = cs
        ? (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0)
        : 0;
      return Math.max(0, parent.clientWidth - pad);
    };

    const applyWidths = () => {
      const available = parentContentWidth();
      if (available <= 0) return;
      const widths = getMostLeastShrinkWidths(available, T);
      const { labelFontSize, cardFontSize, cardLineHeight } = getMostLeastTypeScale(
        { labelWidth: widths.labelWidth, cardWidth: widths.cardWidth },
        T,
      );
      root.style.setProperty('--ml-label-w', `${widths.labelWidth}px`);
      root.style.setProperty('--ml-label-h', `${widths.labelHeight}px`);
      root.style.setProperty('--ml-bar-empty-w', `${widths.emptyBarWidth}px`);
      root.style.setProperty('--ml-bar-empty-h', `${widths.emptyBarHeight}px`);
      root.style.setProperty('--ml-bar-filled-w', `${widths.filledBarWidth}px`);
      root.style.setProperty('--ml-label-fs', `${labelFontSize}px`);
      root.style.setProperty('--ml-card-fs', `${cardFontSize}px`);
      root.style.setProperty('--ml-card-lh', String(cardLineHeight));
      root.style.setProperty('--ml-card-h', `${widths.cardHeight}px`);
      const cards = root.querySelectorAll('.pearson-dnd-ml-tray .pearson-dnd-ml-card');
      let maxH = widths.cardHeight;
      cards.forEach((card) => {
        maxH = Math.max(maxH, Math.ceil(card.scrollHeight));
      });
      root.style.setProperty('--ml-card-h', `${maxH}px`);
      root.style.setProperty('--ml-bar-filled-h', `${maxH}px`);
    };

    const cancelShrinkRaf = () => {
      if (shrinkRafRef.current) {
        cancelAnimationFrame(shrinkRafRef.current);
        shrinkRafRef.current = null;
      }
    };

    observer = new ResizeObserver(() => {
      if (shrinkRafRef.current) return;
      shrinkRafRef.current = requestAnimationFrame(() => {
        shrinkRafRef.current = null;
        applyWidths();
      });
    });
    if (root.parentElement) observer.observe(root.parentElement);
    observer.observe(root);
    applyWidths();

    return () => {
      if (observer) observer.disconnect();
      cancelShrinkRaf();
    };
  }, [T, questionId, compactLayout]);

  const startDrag = (e, { source, optionId, fromSlot, text }) => {
    if (readOnly) return;
    if (e.button !== undefined && e.button !== 0) return;
    const tileRect = e.currentTarget.getBoundingClientRect();
    const ghostTransform = isMostLeastGhostCoarse() ? T.ynGhostTransformCoarse : T.ynGhostTransformFine;
    /* Capture on the root, not the card, so nested tray/slots overflow cannot steal the pointer. */
    const captureEl = rootRef.current || e.currentTarget;
    const ghostPayload = {
      x: e.clientX,
      y: e.clientY,
      text,
      width: tileRect.width,
      height: tileRect.height,
      transform: ghostTransform,
    };
    const onMove = (ev) => handlersRef.current.onPointerMoveWindow(ev);
    const onUp = (ev) => handlersRef.current.onPointerUpWindow(ev);
    const onCancel = (ev) => handlersRef.current.onPointerCancelWindow(ev);
    dragRef.current = {
      source,
      optionId,
      fromSlot: fromSlot || null,
      text,
      originX: e.clientX,
      originY: e.clientY,
      started: false,
      pointerId: e.pointerId,
      tileWidth: tileRect.width,
      tileHeight: tileRect.height,
      ghostTransform,
      captureEl,
      onMove,
      onUp,
      onCancel,
    };
    pendingGhostRef.current = ghostPayload;
    setGhost(ghostPayload);
    if (typeof document !== 'undefined') document.body.style.cursor = 'grabbing';
    if (captureEl && captureEl.addEventListener) {
      captureEl.addEventListener('pointermove', onMove, { passive: false });
      captureEl.addEventListener('pointerup', onUp);
      captureEl.addEventListener('pointercancel', onCancel);
    }
    if (captureEl && captureEl.setPointerCapture) {
      try { captureEl.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    }
  };

  const renderActionCard = (opt, { key, source, fromSlot } = {}) => {
    const html = typeof prepareExamHtml === 'function' ? prepareExamHtml(opt.text || '') : (opt.text || '');
    const text = plainText(opt.text || '');
    return (
      <div
        key={key}
        className="pearson-dnd-ml-card"
        data-ml-tray-option={source === 'tray' ? String(opt.id) : undefined}
        onPointerDown={readOnly ? undefined : (e) => startDrag(e, {
          source,
          optionId: String(opt.id),
          fromSlot,
          text,
        })}
        aria-label={source === 'tray' ? `${text}, draggable` : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          background: T.ynTileBg,
          border: source === 'bar' ? 'none' : `1px solid ${T.ynTileBorder}`,
          color: T.ynTileText,
          fontFamily: T.fontUi,
          fontWeight: 400,
          textAlign: 'center',
          userSelect: 'none',
          touchAction: readOnly ? undefined : 'none',
          cursor: readOnly ? 'default' : 'grab',
          overflow: 'visible',
          overflowWrap: 'break-word',
          wordBreak: 'normal',
          padding: `${T.mlCardPadY}px ${T.mlCardPadX}px`,
        }}
      >
        <span
          className="mathjax-process tex2jax_process"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    );
  };

  const renderSlotRow = (slot, label) => {
    const filledId = slot === 'most' ? mostId : leastId;
    const filled = Boolean(filledId);
    const filledOpt = filled ? optionById(filledId) : null;
    const filledPlain = filledOpt ? plainText(filledOpt.text || '') : '';
    const stateLabel = filled ? `${label}, currently ${filledPlain}` : `${label}, empty`;
    return (
      <div className="pearson-dnd-ml-row" style={{ display: 'flex', alignItems: 'stretch', gap: T.mlLabelBarGap }}>
        <div
          className="pearson-dnd-ml-label"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            background: T.ynTileBg,
            border: `1px solid ${T.ynTileBorder}`,
            color: T.ynTileText,
            fontFamily: T.fontUi,
            fontWeight: 400,
            textAlign: 'center',
            overflow: 'visible',
            overflowWrap: 'break-word',
            wordBreak: 'normal',
            userSelect: 'none',
          }}
        >
          {slotLabelLines(label).map((line, idx) => (
            <React.Fragment key={line}>
              {idx > 0 ? <br /> : null}
              {line}
            </React.Fragment>
          ))}
        </div>
        <div
          data-ml-dropzone-slot={slot}
          className={`pearson-dnd-ml-dropzone${filled ? ' pearson-dnd-ml-dropzone--filled' : ''}`}
          aria-label={stateLabel}
          style={{
            boxSizing: 'border-box',
            background: filled ? T.ynTileBg : dropzoneEmptyBg,
            border: filled ? `1px solid ${T.ynTileBorder}` : 'none',
            touchAction: readOnly ? undefined : 'none',
            overflow: 'visible',
          }}
        >
          {filled && filledOpt ? renderActionCard(filledOpt, { source: 'bar', fromSlot: slot }) : null}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={rootRef}
      className={`pearson-dnd-ml-root${compactLayout ? ' pearson-dnd-ml-root--compact' : ''}${ghost ? ' pearson-dnd-ml-root--dragging' : ''}`}
      style={{
        width: '100%',
        maxWidth: compactLayout ? 'none' : restPx,
        height: compactLayout ? '100%' : undefined,
        minHeight: compactLayout ? 0 : undefined,
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: T.mlRowGap,
        position: 'relative',
        boxSizing: 'border-box',
        alignItems: compactLayout ? 'stretch' : 'flex-start',
      }}
    >
      <style>{`
        .pearson-dnd-ml-root {
          position: relative;
          --ml-label-w: ${T.mlLabelRestWidth}px;
          --ml-label-h: ${T.mlLabelRestHeight}px;
          --ml-bar-empty-w: ${T.mlBarRestWidth}px;
          --ml-bar-empty-h: ${T.mlBarRestHeight}px;
          --ml-bar-filled-w: ${T.mlBarRestWidth}px;
          --ml-bar-filled-h: ${T.mlCardRestHeight}px;
          --ml-card-h: ${T.mlCardRestHeight}px;
          --ml-label-fs: ${T.mlLabelFontRest}px;
          --ml-card-fs: ${T.mlCardFontRest}px;
          --ml-card-lh: ${T.mlCardLineHeightRest};
        }
        .pearson-dnd-ml-label {
          width: var(--ml-label-w);
          min-height: var(--ml-label-h);
          height: auto;
          flex: 0 0 var(--ml-label-w);
          font-size: var(--ml-label-fs);
          line-height: 1.2;
          overflow: visible;
          overflow-wrap: break-word;
          word-break: normal;
        }
        .pearson-dnd-ml-dropzone {
          width: var(--ml-bar-empty-w);
          min-height: var(--ml-bar-empty-h);
          height: var(--ml-bar-empty-h);
          flex: 0 0 var(--ml-bar-empty-w);
        }
        .pearson-dnd-ml-dropzone--filled {
          width: var(--ml-bar-filled-w);
          min-height: var(--ml-card-h);
          height: auto;
          flex: 0 0 var(--ml-bar-filled-w);
        }
        .pearson-dnd-ml-dropzone .pearson-dnd-ml-card {
          width: 100%;
          min-height: var(--ml-card-h);
          height: auto;
          border: none;
        }
        .pearson-dnd-ml-tray {
          margin-left: ${T.mlTrayIndent}px;
          width: calc(var(--ml-label-w) + ${T.mlLabelBarGap}px + max(var(--ml-bar-empty-w), var(--ml-bar-filled-w)) - ${T.mlTrayIndent}px);
          box-sizing: border-box;
          padding: ${T.mlTrayPad}px;
        }
        .pearson-dnd-ml-tray .pearson-dnd-ml-card {
          width: 100%;
          min-height: var(--ml-card-h);
          height: auto;
          font-size: var(--ml-card-fs);
          line-height: var(--ml-card-lh);
          overflow: visible;
          overflow-wrap: break-word;
          word-break: normal;
          box-sizing: border-box;
        }
        .pearson-dnd-ml-dropzone .pearson-dnd-ml-card {
          font-size: var(--ml-card-fs);
          line-height: var(--ml-card-lh);
          overflow: visible;
          overflow-wrap: break-word;
          word-break: normal;
        }
        .pearson-dnd-ml-root--compact {
          height: 100%;
          min-height: 0;
        }
        .pearson-dnd-ml-root--compact .pearson-dnd-ml-slots {
          flex: 0 1 auto;
          max-height: 48%;
          overflow-y: auto;
          min-height: 0;
          width: 100%;
          -webkit-overflow-scrolling: touch;
        }
        .pearson-dnd-ml-root--compact .pearson-dnd-ml-row {
          width: 100%;
        }
        .pearson-dnd-ml-root--compact .pearson-dnd-ml-dropzone,
        .pearson-dnd-ml-root--compact .pearson-dnd-ml-dropzone--filled {
          flex: 1 1 auto;
          width: auto;
          min-width: 0;
        }
        .pearson-dnd-ml-root--compact .pearson-dnd-ml-tray {
          margin-left: 0;
          width: 100%;
          align-self: stretch;
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }
        .pearson-dnd-ml-root--compact.pearson-dnd-ml-root--dragging .pearson-dnd-ml-slots,
        .pearson-dnd-ml-root--compact.pearson-dnd-ml-root--dragging .pearson-dnd-ml-tray {
          overflow: hidden;
          touch-action: none;
        }
      `}</style>
      <div
        ref={slotsRef}
        className="pearson-dnd-ml-slots pearson-dnd-ml-rows"
        style={{ display: 'flex', flexDirection: 'column', gap: T.mlRowGap }}
      >
        {renderSlotRow('most', 'Most Appropriate')}
        {renderSlotRow('least', 'Least Appropriate')}
      </div>
      {!readOnly ? (
        <div
          ref={trayRef}
          className="pearson-dnd-ml-tray"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            background: trayBg,
            alignSelf: compactLayout ? 'stretch' : 'flex-start',
          }}
        >
          {list.map((opt) => renderActionCard(opt, { key: String(opt.id), source: 'tray' }))}
        </div>
      ) : null}
      {ghost ? createPortal(
        <div
          aria-hidden="true"
          data-testid="pearson-ml-ghost"
          style={{
            position: 'fixed',
            left: `${ghost.x}px`,
            top: `${ghost.y}px`,
            transform: ghost.transform,
            width: ghost.width,
            height: ghost.height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: T.ynGhostFill,
            opacity: 1,
            border: `2px solid ${T.ynGhostOutline}`,
            color: T.ynGhostText,
            fontFamily: T.fontUi,
            fontSize: 16,
            lineHeight: 1.2,
            textAlign: 'center',
            boxSizing: 'border-box',
            pointerEvents: 'none',
            zIndex: T.ynGhostZIndex,
            overflow: 'visible',
            overflowWrap: 'anywhere',
            padding: '4px 8px',
          }}
        >
          {ghost.text}
        </div>,
        document.body
      ) : null}
    </div>
  );
}
